import { createAction } from 'redux-starter-kit';
import { get, post, request } from '../../../../utility/via-fetch';
import { requestStart, requestFailed, requestSucceded } from '../../../domain/requests/RequestActions';
import { addMessage } from '../../application/messageUiActions';
import { putEmailRecipient, putEmailRecipients, removeEmailRecipient } from '../../../domain/communication/emailRecipientActions';

/**
 * An action that signifies an email recipient is being currently edited.
 * @example dispatch(editEmailRecipient(emailRecipient))
 */
export const editEmailRecipient = createAction('EDIT_EMAIL_RECIPIENT');

/**
 * An action that signifies an email recipient is no longer being edited.
 * @example dispatch(cancelEditEmailRecipient())
 */
export const cancelEditEmailRecipient = createAction('CANCEL_EDIT_EMAIL_RECIPIENT');

/**
 * An action that signifies a request to load all emailRecipients has been made.
 * @example dispatch(fetchEmailRecipients())
 */
export const fetchEmailRecipients = createAction('FETCH_EMAIL_RECIPIENTS');

/**
 * An action for loading all the emailRecipients.
 * @example dispatch(requestEmailRecipients())
 */
export function requestEmailRecipients(manageCompany) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchEmailRecipients.toString()));
    dispatch(fetchEmailRecipients(requestId));

    try {
      const emailRecipients = await get('/api/communication/email-recipients/company/' + manageCompany, undefined, {});
      dispatch(putEmailRecipients(emailRecipients));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies that a fetch to add a new email recipient has been
 * made in the email recipient configuration screen. This should pass through the request id.
 * @example dispatch(fetchAddEmailRecipient(requestId))
 */
export const fetchAddEmailRecipient = createAction('FETCH_CREATE_EMAIL_RECIPIENT');

/**
 * An action for creating an email recipient on the server. 
 * @param {object} emailRecipient The email recipient to be created.
 * @param {intl} intl The intl instance being used in the application as retrieved by injectintl
 * @example dispatch(requestAddEmailRecipient(emailRecipient));
 */
export function requestAddEmailRecipient(newEmailRecipient, manageCompany) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAddEmailRecipient.toString()));
    dispatch(fetchAddEmailRecipient(requestId));
    try {
      const recipientId = await post('/api/communication/email-recipients/company/' + manageCompany, {}, newEmailRecipient);
      
      dispatch(putEmailRecipient({ ...newEmailRecipient, recipientId: recipientId}));
      dispatch(cancelEditEmailRecipient());
      dispatch(requestSucceded(requestId, newEmailRecipient.emailAddress));
      dispatch(addMessage('success', 'itemSaved', { item: newEmailRecipient.emailAddress }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies that a fetch to delete an email recipient
 * has been made in the emailRecipients configuration screen.
 * This should pass through the request id.
 * @example dispatch(deleteEmailRecipient(requestId));
 */
export const deleteEmailRecipient = createAction('DELETE_EMAIL_RECIPIENT');

/**
 * An action for deleting an email recipient on the server.
 * @param {object} emailRecipient The email recipient to be updated.
 */
export function requestDeleteEmailRecipient(recipientId, emailAddress) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(deleteEmailRecipient.toString()));
    dispatch(deleteEmailRecipient(requestId));
    try {
      await request('DELETE', `/api/communication/email-recipients/company/${recipientId}`, {}, {});
      dispatch(removeEmailRecipient(recipientId));
      dispatch(cancelEditEmailRecipient());
      dispatch(requestSucceded(requestId));
      dispatch(addMessage('success', 'itemDeleted', { item: emailAddress }));
    } catch (error) {
      dispatch(requestEmailRecipients());
      dispatch(requestFailed(requestId, error));
    }
  };
}
